/**
 * Format d'envoi du média d'un défi (bucket `challenges` : jpeg, png, webp,
 * mp4, webm). Une vidéo de l'appareil photo de l'iPhone est un fichier
 * QuickTime (.mov) : même conteneur que le mp4, on l'envoie donc en mp4 pour
 * qu'elle soit acceptée et lue partout. Pur, testé.
 */

/** Taille maximale d'un média : limite du bucket (50 Mo). */
export const CHALLENGE_MEDIA_MAX_BYTES = 50 * 1024 * 1024;

export interface ChallengeVideoFormat {
  contentType: 'video/mp4' | 'video/webm';
  ext: 'mp4' | 'webm';
}

const MP4_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/3gpp',
  'video/3gpp2',
]);
const MP4_EXTENSIONS = new Set(['mp4', 'mov', 'm4v', 'qt', '3gp', '3g2']);

/**
 * Format d'envoi d'une vidéo, d'après son type (sans les paramètres de codec)
 * et, à défaut, l'extension de son nom. `null` = format qu'aucun navigateur
 * ne saurait lire.
 */
export function challengeVideoFormat(type: string, name = ''): ChallengeVideoFormat | null {
  const base = type.toLowerCase().split(';')[0]!.trim();
  if (base === 'video/webm') return { contentType: 'video/webm', ext: 'webm' };
  if (MP4_TYPES.has(base)) return { contentType: 'video/mp4', ext: 'mp4' };
  if (base === '' || base === 'application/octet-stream') {
    const ext = name.toLowerCase().split('.').pop() ?? '';
    if (ext === 'webm') return { contentType: 'video/webm', ext: 'webm' };
    if (MP4_EXTENSIONS.has(ext)) return { contentType: 'video/mp4', ext: 'mp4' };
    // un enregistrement sans type connu : celui de la plupart des téléphones
    if (ext === '' || ext === name.toLowerCase()) return { contentType: 'video/mp4', ext: 'mp4' };
  }
  return null;
}

export class ChallengeMediaError extends Error {}

/** Message lisible si le média ne peut pas être envoyé, sinon null. */
export function challengeMediaIssue(
  file: Blob & { name?: string },
  mediaType: 'photo' | 'video',
): string | null {
  if (file.size === 0)
    return mediaType === 'video'
      ? 'La vidéo est vide : recommence l’enregistrement.'
      : 'La photo est vide : recommence.';
  if (file.size > CHALLENGE_MEDIA_MAX_BYTES)
    return mediaType === 'video'
      ? 'Vidéo trop lourde (50 Mo au plus) : filme une vidéo plus courte, ou enregistre-la ici avec le bouton rouge.'
      : 'Photo trop lourde (50 Mo au plus).';
  if (mediaType === 'video' && !challengeVideoFormat(file.type, file.name ?? ''))
    return 'Format vidéo non pris en charge : enregistre la vidéo ici avec le bouton rouge.';
  return null;
}
